/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Tiny client-side fetch cache so SPA navigation between pages reuses
 * already-fetched data instead of hitting the network every click.
 *
 * Cache entries are keyed by URL and expire after `TTL_MS`. After expiry the
 * next request refetches but still returns the stale value synchronously so
 * the caller can render immediately while the new request flies (a
 * stale-while-revalidate pattern, opt-in per call).
 *
 * Not a replacement for SWR/React Query — just enough to kill the visible
 * spinner on revisits to the same route in a normal browsing session.
 */

const TTL_MS = 60_000; // 60 seconds

interface CacheEntry<T> {
  value: T;
  fetchedAt: number;
  inflight?: Promise<T>;
}

const cache = new Map<string, CacheEntry<unknown>>();

/**
 * Fetch JSON from `url` with a 60-second client-side cache.
 *
 * - First call for a URL: hits the network, caches the response, returns the
 *   parsed JSON.
 * - Subsequent calls within TTL: returns the cached value instantly without
 *   touching the network.
 * - In-flight de-dup: parallel calls for the same URL share one network
 *   request.
 *
 * The caller still gets a Promise so call sites can `await` uniformly.
 */
export async function cachedFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const now = Date.now();
  const entry = cache.get(url) as CacheEntry<T> | undefined;
  if (entry && now - entry.fetchedAt < TTL_MS) {
    return entry.value;
  }
  if (entry?.inflight) {
    return entry.inflight;
  }
  const inflight = (async () => {
    const res = await fetch(url, init);
    if (!res.ok) throw new Error(`fetch ${url} failed: ${res.status}`);
    const value = (await res.json()) as T;
    cache.set(url, { value, fetchedAt: Date.now() });
    return value;
  })();
  cache.set(url, { value: entry?.value as T, fetchedAt: entry?.fetchedAt ?? 0, inflight });
  try {
    return await inflight;
  } catch (err) {
    // On error, drop the in-flight marker but keep any prior cached value
    // so callers can fall back if they want.
    if (cache.has(url)) {
      const e = cache.get(url) as CacheEntry<T>;
      delete e.inflight;
      if (!e.fetchedAt) cache.delete(url);
    }
    throw err;
  }
}

/** Synchronous read of the cached value if present and fresh. */
export function readCache<T>(url: string): T | undefined {
  const entry = cache.get(url) as CacheEntry<T> | undefined;
  if (!entry) return undefined;
  if (Date.now() - entry.fetchedAt >= TTL_MS) return undefined;
  return entry.value;
}

/** Drop a single URL from the cache. */
export function invalidate(url: string): void {
  cache.delete(url);
}

/** Clear the entire cache (used after admin saves trigger a re-bake). */
export function invalidateAll(): void {
  cache.clear();
}
